/**
 * MixingRecipeMasterPage — Phase 2 Sprint C UI
 * ─────────────────────────────────────────────────────────────────────────────
 * Browser + editor for the 60 seeded mixing recipes (Sec 4 of the cotton
 * mixing standards spec). Filterable by yarn type. Each row shows the
 * recipe label "A25/B45/C20/R10" + status pill + grade-percentage bar.
 *
 * Edit drawer lets a mill admin tune grade percentages per recipe (e.g.
 * shift A from 25 to 30 for export contracts). Total must sum to 100.
 */
import { useState } from 'react'
import {
  Alert, App, Button, Card, Col, Drawer, Empty, Form, InputNumber, Row,
  Select, Space, Table, Tag, Tooltip, Typography,
} from 'antd'
import { EditOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import PageHeader from '@/shared/components/PageHeader'
import { mixingRecipeApi } from '@/features/baleManagement/api'

const { Text } = Typography

type GradeLine = { id?: string; grade_code: string; pct: number }

type Recipe = {
  id: string
  count_ne: number
  yarn_type: string
  status: 'ALLOWED' | 'SPECIAL_APPROVAL' | 'NOT_RECOMMENDED' | 'BLOCKED'
  recipe_label: string | null
  notes: string | null
  is_active: boolean
  lines: GradeLine[]
}

const STATUS_TAG_COLOR: Record<Recipe['status'], string> = {
  ALLOWED: 'green',
  SPECIAL_APPROVAL: 'gold',
  NOT_RECOMMENDED: 'orange',
  BLOCKED: 'red',
}

const STATUS_LABEL: Record<Recipe['status'], string> = {
  ALLOWED: 'Allowed',
  SPECIAL_APPROVAL: 'Special approval',
  NOT_RECOMMENDED: 'Not recommended',
  BLOCKED: 'Blocked',
}

const GRADE_CODES = ['E1', 'E2', 'A', 'B', 'C', 'D', 'R'] as const

const GRADE_COLOR: Record<string, string> = {
  E1: '#722ed1', E2: '#9254de',
  A: '#52c41a', B: '#1677ff',
  C: '#faad14', D: '#fa8c16',
  R: '#8c8c8c',
}

function GradeMixBar({ lines }: { lines: GradeLine[] }) {
  if (lines.length === 0) return <Text type="secondary" style={{ fontSize: 11 }}>blocked</Text>
  return (
    <div style={{
      display: 'flex', height: 24, borderRadius: 4, overflow: 'hidden',
      border: '1px solid #f0f0f0', minWidth: 220,
    }}>
      {lines.map((L, i) => (
        <Tooltip key={L.id ?? i} title={`${L.grade_code} = ${L.pct}%`}>
          <div style={{
            width: `${L.pct}%`,
            background: GRADE_COLOR[L.grade_code] || '#888',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 11, fontWeight: 600,
          }}>
            {L.pct >= 8 ? `${L.grade_code} ${L.pct}` : ''}
          </div>
        </Tooltip>
      ))}
    </div>
  )
}

export default function MixingRecipeMasterPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [yarnTypeFilter, setYarnTypeFilter] = useState<string | undefined>()
  const [editing, setEditing] = useState<Recipe | null>(null)
  const [form] = Form.useForm()

  const recipesQ = useQuery<Recipe[]>({
    queryKey: ['mixing-recipes', yarnTypeFilter],
    queryFn: () => mixingRecipeApi.list({
      yarn_type: yarnTypeFilter,
      only_active: true,
    }),
  })

  const update = useMutation({
    mutationFn: (data: { id: string; payload: Record<string, unknown> }) =>
      mixingRecipeApi.update(data.id, data.payload),
    onSuccess: () => {
      message.success('Recipe updated')
      qc.invalidateQueries({ queryKey: ['mixing-recipes'] })
      setEditing(null)
    },
    onError: (e: Error) => message.error(e.message),
  })

  const startEdit = (r: Recipe) => {
    setEditing(r)
    form.setFieldsValue({
      status: r.status,
      recipe_label: r.recipe_label,
      notes: r.notes,
      lines: r.lines.map(L => ({ grade_code: L.grade_code, pct: Number(L.pct) })),
    })
  }

  const submitEdit = () => {
    form.validateFields().then((values) => {
      if (!editing) return
      const lines = (values.lines || []) as GradeLine[]
      const total = lines.reduce((sum, L) => sum + Number(L.pct || 0), 0)
      if (lines.length > 0 && Math.abs(total - 100) > 0.01) {
        message.error(`Grade percentages must sum to 100% (currently ${total.toFixed(1)}%)`)
        return
      }
      // Auto-generate label if user didn't set one
      const label = values.recipe_label
        || lines.map(L => `${L.grade_code}${L.pct}`).join('/')
      update.mutate({
        id: editing.id,
        payload: {
          status: values.status,
          recipe_label: label,
          notes: values.notes,
          lines,
        },
      })
    })
  }

  return (
    <div style={{ padding: 16 }}>
      <PageHeader
        title="Mixing Recipe Master"
        subtitle="Per count × yarn-type, the prescribed grade percentages (Sec 4 of spec)"
        extra={
          <Select
            value={yarnTypeFilter}
            onChange={setYarnTypeFilter}
            placeholder="Filter by yarn type"
            allowClear
            style={{ width: 200 }}
            options={[
              { label: 'Carded compact', value: 'CARDED_COMPACT' },
              { label: 'Compact', value: 'COMPACT' },
              { label: 'Siro', value: 'SIRO' },
              { label: 'Combed', value: 'COMBED' },
              { label: 'Combed compact', value: 'COMBED_COMPACT' },
            ]}
          />
        }
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Two ways to build a laydown"
        description={
          <span style={{ fontSize: 13 }}>
            <strong>HVI Matrix</strong> picks bales by Mic/SCI proximity to a recipe target.
            <strong> Recipe-driven</strong> picks bales to match these prescribed grade
            percentages exactly. Use the <em>Recipe</em> mode toggle on Quick Laydown
            or the Suggest drawer to switch.
          </span>
        }
      />

      <Card styles={{ body: { padding: 0 } }}>
        {recipesQ.data && recipesQ.data.length === 0 ? (
          <Empty description="No recipes match the filter" style={{ padding: 32 }} />
        ) : (
          <Table
            rowKey="id"
            loading={recipesQ.isLoading}
            dataSource={recipesQ.data || []}
            pagination={{ pageSize: 30, showSizeChanger: false }}
            columns={[
              {
                title: 'Count', dataIndex: 'count_ne', width: 80, align: 'center',
                sorter: (a, b) => a.count_ne - b.count_ne,
                defaultSortOrder: 'ascend',
                render: (v: number) => <Tag>{v}s</Tag>,
              },
              {
                title: 'Yarn type', dataIndex: 'yarn_type', width: 160,
                render: (v: string) => (
                  <Text style={{ fontSize: 12 }}>{v.replace(/_/g, ' ').toLowerCase()}</Text>
                ),
              },
              {
                title: 'Recipe', dataIndex: 'recipe_label', width: 180,
                render: (v: string | null, r: Recipe) =>
                  r.status === 'BLOCKED'
                    ? <Tag color="red">BLOCKED</Tag>
                    : <Text strong style={{ fontFamily: 'monospace' }}>{v ?? '—'}</Text>,
              },
              {
                title: 'Grade mix', width: 280,
                render: (_: unknown, r: Recipe) => <GradeMixBar lines={r.lines} />,
              },
              {
                title: 'Status', dataIndex: 'status', width: 150,
                render: (v: Recipe['status']) => (
                  <Tag color={STATUS_TAG_COLOR[v]}>{STATUS_LABEL[v]}</Tag>
                ),
              },
              {
                title: '', width: 60, align: 'center',
                render: (_: unknown, r: Recipe) => (
                  <Tooltip title="Edit recipe">
                    <Button
                      type="text" icon={<EditOutlined />}
                      onClick={() => startEdit(r)}
                    />
                  </Tooltip>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Drawer
        title={
          editing ? (
            <Space>
              <Tag>{editing.count_ne}s</Tag>
              <Text>{editing.yarn_type.replace(/_/g, ' ').toLowerCase()}</Text>
            </Space>
          ) : 'Edit recipe'
        }
        open={editing !== null}
        onClose={() => setEditing(null)}
        width={520}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="primary" onClick={submitEdit} loading={update.isPending}>
              Save
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Status" name="status" rules={[{ required: true }]}>
            <Select options={[
              { label: 'Allowed', value: 'ALLOWED' },
              { label: 'Special approval', value: 'SPECIAL_APPROVAL' },
              { label: 'Not recommended', value: 'NOT_RECOMMENDED' },
              { label: 'Blocked', value: 'BLOCKED' },
            ]} />
          </Form.Item>
          <Form.Item label="Recipe label (auto if empty)" name="recipe_label"
            extra="Compact form e.g. A25/B45/C20/R10. Leave empty to regenerate from grade lines.">
            <Select mode="tags" maxCount={1}
              placeholder="A25/B45/C20/R10" style={{ width: '100%' }} />
          </Form.Item>

          <Text strong>Grade percentages (must sum to 100%)</Text>
          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Row key={key} gutter={8} align="middle" style={{ marginTop: 8 }}>
                    <Col flex="120px">
                      <Form.Item {...rest} name={[name, 'grade_code']} rules={[{ required: true }]} noStyle>
                        <Select options={GRADE_CODES.map(g => ({ label: g, value: g }))} />
                      </Form.Item>
                    </Col>
                    <Col flex="auto">
                      <Form.Item {...rest} name={[name, 'pct']} rules={[{ required: true }]} noStyle>
                        <InputNumber min={0} max={100} step={1} addonAfter="%" style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col flex="40px">
                      <Button type="text" icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" icon={<PlusOutlined />}
                  onClick={() => add({ grade_code: 'A', pct: 0 })}
                  style={{ width: '100%', marginTop: 8 }}>
                  Add grade line
                </Button>
              </>
            )}
          </Form.List>

          <Form.Item label="Notes" name="notes" style={{ marginTop: 16 }}>
            <Select mode="tags" maxCount={1} placeholder="Optional" />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
